import api from "../utils/axiosConfig";
import {
  USE_MOCK_DATA,
  loadMock,
  getMockTransaction,
  setMockTransaction,
} from "../utils/mock";

// ==================== Types ====================

export type CreateTransactionParams = {
  amount: number;
  remarks?: string;
  payment_method: "bank_transfer" | "duitnow";
};

export type TransactionData = {
  transactionId: string;
  amount: number;
  reference_number: string;
  status: "pending" | "uploaded" | "approved" | "rejected";
  remarks?: string;
  payment_method: string;
  rejection_reason?: string;
  created_at: string;
  verified_at?: string;
};

export type BankInfo = {
  type: "bank_transfer" | "duitnow";
  bank_name?: string;        // Only for bank_transfer
  account_name: string;
  account_number?: string;   // Only for bank_transfer
  phone_no?: string;         // Only for duitnow
};

export type BankInfoResponse = {
  transactionId: string;
  amount: number;
  reference_number: string;
  payment_method: string;
  status: string;
  bank_info: BankInfo;
  instructions: string[];
};

export type ReceiptData = {
  receipt_id: string;
  file_url?: string;
  uploaded_at?: string;
};

export type TransactionDetailsData = {
  transactionId: string;
  amount: number;
  remarks?: string;
  payment_method: string;
  reference_number: string;
  status: "pending" | "uploaded" | "approved" | "rejected";
  rejection_reason?: string;
  bank_info?: BankInfo;
  receipt?: ReceiptData;
  verified_at?: string;
  created_at: string;
};

// API Response Types
export type CreateTransactionApiResponse = {
  status: number;
  system: string;
  message: string;
  data: {
    transactionId: string;
    amount: number;
    reference_number: string;
    status: string;
    created_at: string;
  };
};

export type BankInfoApiResponse = {
  status: number;
  system: string;
  message: string;
  data: BankInfoResponse;
};

// ==================== Mock Helpers ====================

const applyMockOverride = <T extends TransactionData | TransactionDetailsData>(
  transaction: T
): T => ({ ...transaction, ...getMockTransaction(transaction.transactionId) });

const findMockTransaction = async <T extends { transactionId: string }>(
  file: string,
  transactionId: string
): Promise<T> => {
  const records = await loadMock<T[]>(file);
  const match = records.find(
    (record) => record.transactionId === transactionId
  );
  if (!match) {
    throw new Error(`Transaction ${transactionId} not found`);
  }
  return match;
};

// ==================== API Functions ====================

/**
 * Create a new payment transaction
 * POST /transaction/create
 */
export const createTransaction = async (
  params: CreateTransactionParams
): Promise<CreateTransactionApiResponse> => {
  if (USE_MOCK_DATA) {
    const records = await loadMock<TransactionDetailsData[]>(
      "transaction-details.json"
    );
    const candidates = records.filter(
      (record) => record.payment_method === params.payment_method
    );
    const slot =
      candidates.find(
        (record) => applyMockOverride(record).status === "pending"
      ) || candidates[0];
    if (!slot) {
      throw new Error("No pending mock transaction available");
    }
    setMockTransaction(slot.transactionId, {
      amount: params.amount,
      remarks: params.remarks,
      status: "pending",
    });
    return {
      status: 1,
      system: "transaction",
      message: "Transaction created successfully",
      data: {
        transactionId: slot.transactionId,
        amount: params.amount,
        reference_number: slot.reference_number,
        status: "pending",
        created_at: new Date().toISOString(),
      },
    };
  }

  try {
    const response = await api.post<CreateTransactionApiResponse>(
      "/transaction/create",
      params
    );
    return response.data;
  } catch (error) {
    console.error("Create transaction error:", error);
    throw error;
  }
};

/**
 * Get bank account details for making transfer
 * GET /transaction/bank-info/:transactionId
 */
export const getBankInfo = async (
  transactionId: string
): Promise<BankInfoApiResponse> => {
  if (USE_MOCK_DATA) {
    const match = await findMockTransaction<BankInfoResponse>(
      "bank-info.json",
      transactionId
    );
    const override = getMockTransaction(transactionId);
    return {
      status: 1,
      system: "transaction",
      message: "Bank information retrieved successfully",
      data: { ...match, ...override },
    };
  }

  try {
    const response = await api.get<BankInfoApiResponse>(
      `/transaction/bank-info/${transactionId}`
    );
    return response.data;
  } catch (error) {
    console.error("Get bank info error:", error);
    throw error;
  }
};
